import { useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { User, Lock, Save, Loader2, Building2 } from 'lucide-react'
import CarrierAppointmentsEditor, { CarrierAppointmentInput, CarrierOption } from '@/components/CarrierAppointmentsEditor'

interface UserData {
  id: number
  email: string
  first_name: string
  last_name: string
  role: string
  created_at: string
}

interface AgentData {
  id: number
  carrier_appointments: Array<{
    id: number
    carrier_id: number
    carrier_name: string | null
    producer_number: string
  }>
}

interface Props extends PageProps {
  user: UserData
  agent: AgentData | null
  carriers: CarrierOption[]
  minimum_password_length: number
}

export default function ProfileEdit({ user, agent, carriers, minimum_password_length }: Props) {
  const hasAgent = !!agent
  type TabType = 'profile' | 'password' | 'carriers'
  const [activeTab, setActiveTab] = useState<TabType>('profile')
  const [appointments, setAppointments] = useState<CarrierAppointmentInput[]>(
    (agent?.carrier_appointments ?? []).map(a => ({
      id: a.id,
      carrier_id: a.carrier_id,
      producer_number: a.producer_number
    }))
  )
  const [saving, setSaving] = useState(false)
  
  const [profileForm, setProfileForm] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email
  })

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    password: '',
    password_confirmation: ''
  })
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({})

  const [emailPassword, setEmailPassword] = useState('')
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({})

  // Whoever controls the email address can reset the password and take the
  // account over, so the server asks for the current password before moving it.
  // Renaming yourself stays a one-field edit.
  const emailChanged =
    profileForm.email.trim().toLowerCase() !== user.email.trim().toLowerCase()

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setProfileErrors({})
    router.patch(
      '/profile',
      { user: profileForm, current_password: emailChanged ? emailPassword : undefined },
      {
        onSuccess: () => setEmailPassword(''),
        onError: (errors) => setProfileErrors(errors as Record<string, string>),
        onFinish: () => setSaving(false)
      }
    )
  }

  const profileError = (field: string) => profileErrors[field] || profileErrors[`user.${field}`]

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setPasswordErrors({})
    router.patch('/profile/password', { user: passwordForm }, {
      // Only wipe the fields once the change actually went through — on a
      // failure the user needs to correct one field, not retype all three.
      onSuccess: () => {
        setPasswordForm({ current_password: '', password: '', password_confirmation: '' })
      },
      onError: (errors) => setPasswordErrors(errors as Record<string, string>),
      onFinish: () => setSaving(false)
    })
  }

  // The server keys errors by attribute; the reset-password screen also accepts
  // a `user.` prefix, so tolerate both here.
  const passwordError = (field: string) =>
    passwordErrors[field] || passwordErrors[`user.${field}`]

  const passwordInputClass = (field: string) =>
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 ${
      passwordError(field)
        ? 'border-status-error focus:border-status-error'
        : 'border-chestnut-charcoal/20 focus:border-chestnut-forest'
    }`

  const handleCarriersSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      agent: {
        carrier_appointments_attributes: appointments
          .filter(a => a.id || (!a._destroy && (a.carrier_id !== '' || a.producer_number.trim() !== '')))
          .map(a => ({
            id: a.id,
            carrier_id: a.carrier_id === '' ? null : a.carrier_id,
            producer_number: a.producer_number,
            _destroy: a._destroy ? '1' : undefined
          }))
      }
    }
    router.patch('/profile/carrier_appointments', payload, {
      onFinish: () => setSaving(false)
    })
  }

  return (
    <AppLayout>
      <Head title="Profile Settings" />

      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Profile Settings</h1>
          <p className="text-chestnut-charcoal/70 mt-1">Manage your account settings and password</p>
        </div>

        <div className="border-b border-chestnut-charcoal/10">
          <nav className="flex gap-6 -mb-px">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'profile'
                  ? 'border-chestnut-forest text-chestnut-forest'
                  : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
              }`}
            >
              <User className="h-4 w-4" />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'password'
                  ? 'border-chestnut-forest text-chestnut-forest'
                  : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
              }`}
            >
              <Lock className="h-4 w-4" />
              Password
            </button>
            {hasAgent && (
              <button
                onClick={() => setActiveTab('carriers')}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'carriers'
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                <Building2 className="h-4 w-4" />
                Carrier Appointments
              </button>
            )}
          </nav>
        </div>

        {activeTab === 'carriers' && hasAgent && (
          <form onSubmit={handleCarriersSubmit} className="card space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-chestnut-charcoal mb-2">Carrier Appointments</h2>
              <p className="text-sm text-chestnut-charcoal/60 mb-4">
                Add the carriers you write for and your producer / writing number with each. Incoming commissions are auto-matched to you when the writing-agent ID matches any number here. You can have multiple numbers with the same carrier.
              </p>
            </div>

            <CarrierAppointmentsEditor
              appointments={appointments}
              carriers={carriers}
              onChange={setAppointments}
            />

            <div className="flex justify-end pt-2 border-t border-chestnut-gray/40">
              <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Appointments
              </button>
            </div>
          </form>
        )}

        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} className="card space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-chestnut-charcoal mb-4">Profile Information</h2>
              <p className="text-sm text-chestnut-charcoal/60 mb-6">Update your personal information.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">First Name</label>
                <input
                  type="text"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Last Name</label>
                <input
                  type="text"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 focus:border-chestnut-forest"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Email</label>
              <input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 ${
                  profileError('email')
                    ? 'border-status-error focus:border-status-error'
                    : 'border-chestnut-charcoal/20 focus:border-chestnut-forest'
                }`}
                required
              />
              {profileError('email') && (
                <p className="mt-1 text-sm text-status-error">{profileError('email')}</p>
              )}
              <p className="mt-1 text-sm text-chestnut-charcoal/60">
                This is the address you sign in with and the one a password reset is sent to.
              </p>
            </div>

            {emailChanged && (
              <div className="rounded-lg border border-chestnut-gold/40 bg-chestnut-cream/50 p-4">
                <label className="block text-sm font-medium text-chestnut-charcoal mb-1">
                  Confirm your current password
                </label>
                <p className="text-sm text-chestnut-charcoal/60 mb-3">
                  Changing your sign-in address also changes where a password reset goes, so we ask for
                  your password to be sure it is you.
                </p>
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  autoComplete="current-password"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-chestnut-forest/20 ${
                    profileError('current_password')
                      ? 'border-status-error focus:border-status-error'
                      : 'border-chestnut-charcoal/20 focus:border-chestnut-forest'
                  }`}
                  required
                />
                {profileError('current_password') && (
                  <p className="mt-1 text-sm text-status-error">{profileError('current_password')}</p>
                )}
              </div>
            )}

            <div className="bg-chestnut-cream/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-chestnut-charcoal/60">Role:</span>
                  <span className="ml-2 font-medium capitalize">{user.role}</span>
                </div>
                <div>
                  <span className="text-sm text-chestnut-charcoal/60">Member since:</span>
                  <span className="ml-2 font-medium">{new Date(user.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex items-center gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Changes
              </button>
            </div>
          </form>
        )}

        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="card space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-chestnut-charcoal mb-4">Change Password</h2>
              <p className="text-sm text-chestnut-charcoal/60 mb-6">Update your password to keep your account secure.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Current Password</label>
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                className={passwordInputClass('current_password')}
                autoComplete="current-password"
                required
              />
              {passwordError('current_password') && (
                <p className="mt-1 text-sm text-status-error">{passwordError('current_password')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">New Password</label>
              <input
                type="password"
                value={passwordForm.password}
                onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                className={passwordInputClass('password')}
                autoComplete="new-password"
                required
                minLength={minimum_password_length}
              />
              {passwordError('password') ? (
                <p className="mt-1 text-sm text-status-error">{passwordError('password')}</p>
              ) : (
                <p className="mt-1 text-xs text-chestnut-charcoal/60">
                  Minimum {minimum_password_length} characters
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal mb-1">Confirm New Password</label>
              <input
                type="password"
                value={passwordForm.password_confirmation}
                onChange={(e) => setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })}
                className={passwordInputClass('password_confirmation')}
                autoComplete="new-password"
                required
              />
              {passwordError('password_confirmation') && (
                <p className="mt-1 text-sm text-status-error">{passwordError('password_confirmation')}</p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex items-center gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Update Password
              </button>
            </div>
          </form>
        )}
      </div>
    </AppLayout>
  )
}
